import { MouseEvent, PropsWithChildren } from 'react';

import AccessibilityMuiButton, {
  AccessibilityMuiButtonProps,
} from '@pages/AdvancedSearchListCreatorPage/customWidgets/AccessibilityMuiButton';
import {
  DeleteActionType,
  useClearPendingDeleteTarget,
  useSetPendingDeleteTarget,
} from './ConfirmDeleteRuleOrGroupContext';
import { getNodeIdOfDeleteButton } from './util';

const DELETE_ACTION_TYPES: DeleteActionType[] = ['delRule', 'delGroup', 'delRuleGroup'];

function isDeleteActionType(type: string): type is DeleteActionType {
  return (DELETE_ACTION_TYPES as string[]).includes(type);
}

type DeletionInterceptionButtonProps = PropsWithChildren<AccessibilityMuiButtonProps>;

/**
 * This component renders an AccessibilityMuiButton that intercepts the click
 * event and sets the pending delete target (action type + node id) before
 * forwarding the click. The provider's confirm() uses this to decide whether
 * to show the confirmation dialog based on placeholder presence in the target.
 */
export default function DeletionInterceptionButton({
  type,
  onClick,
  ...props
}: DeletionInterceptionButtonProps) {
  const setPendingDeleteTarget = useSetPendingDeleteTarget();
  const clearPendingDeleteTarget = useClearPendingDeleteTarget();

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (isDeleteActionType(type)) {
      const nodeId = getNodeIdOfDeleteButton(event.currentTarget);
      setPendingDeleteTarget?.({ actionType: type, nodeId });
    } else {
      clearPendingDeleteTarget?.();
    }
    onClick(event);
  };

  return <AccessibilityMuiButton {...props} type={type} onClick={handleClick} />;
}
